/* eslint-disable react/prop-types */

import React, { useContext } from 'react';
import { flexRender } from '@tanstack/react-table';
import { Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import AppContext from 'context/Context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BsExclamationCircle } from 'react-icons/bs';
import { format, parse } from 'date-fns';

export const getCommonPinningStyles = (column, allColumns = []) => {
  const isPinned = column.getIsPinned();

  const commonStyles = {
    position: 'relative',
    backgroundColor: 'inherit',
    color: 'inherit',
    minWidth: '50px',
    // width: column.getSize(),
    maxWidth: column.getSize(),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  };

  if (!isPinned) {
    return commonStyles;
  }

  const pinnedColumns = allColumns.filter(
    col => col.getIsPinned() === isPinned
  );
  const columnIndex = allColumns.findIndex(col => col.id === column.id);

  return {
    ...commonStyles,
    position: 'sticky',
    backgroundColor: 'rgb(214, 214, 214)',
    zIndex: 2,
    left: isPinned === 'left' ? `var(--frozen-left-${columnIndex})` : undefined,
    boxShadow:
      isPinned === 'left' &&
      pinnedColumns[pinnedColumns.length - 1].id === column.id
        ? '2px 0 4px -2px rgba(0, 0, 0, 0.3)'
        : 'none'
  };
};

export const HeaderCell = ({ header, table }) => {
  const {
    config: { isRTL }
  } = useContext(AppContext);

  const allLeafColumns = table.getAllLeafColumns();
  const showFilters = table.getState().showFilters;

  // Array of date-related columns
  const dateColumns = ['created on', 'modified on', 'transferred on', 'date'];

  // Check if this is the first filterable column (excluding action)
  const isFirstFilterableColumn = () => {
    const filterableColumns = allLeafColumns.filter(
      col => col.getCanFilter() && col.id !== 'action'
    );
    return (
      filterableColumns.length > 0 &&
      filterableColumns[0].id === header.column.id
    );
  };

  return (
    <div>
      <div
        style={{
          ...getCommonPinningStyles(header.column, allLeafColumns),
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          height: '100%',
          padding: '0rem',
          alignContent: 'flex-start'
        }}
      >
        <span className="text-truncate fs--1">
          {flexRender(header.column.columnDef.header, header.getContext())}
        </span>
        <div className="d-flex align-items-center">
          <Dropdown
            className="font-sans-serif btn-reveal-trigger"
            align={isRTL ? 'start' : 'end'}
          >
            <Dropdown.Toggle
              variant="link"
              size="sm"
              data-boundary="viewport"
              className={`d-flex align-items-center justify-content-between w-100 ${
                header.column.getIsPinned() ? 'text-white' : ''
              }`}
            >
              <FontAwesomeIcon icon="ellipsis-v" className="fs--2 ms-2" />
            </Dropdown.Toggle>
            <Dropdown.Menu className="border py-0">
              <div className="py-2">
                <Dropdown.Item
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {'Sort'}
                </Dropdown.Item>
                {header.column.getIsPinned() !== 'left' && (
                  <Dropdown.Item onClick={() => header.column.pin('left')}>
                    Pin to Left
                  </Dropdown.Item>
                )}
                {header.column.getIsPinned() && (
                  <Dropdown.Item onClick={() => header.column.pin(false)}>
                    Unpin
                  </Dropdown.Item>
                )}
                <Dropdown.Item onClick={() => header.column.setFilterValue('')}>
                  Clear Filter
                </Dropdown.Item>
              </div>
            </Dropdown.Menu>
          </Dropdown>
        </div>
        {header.column.getCanResize() && (
          <div
            onMouseDown={header.getResizeHandler()}
            onTouchStart={header.getResizeHandler()}
            className={`resizer ${
              header.column.getIsResizing() ? 'isResizing' : ''
            }`}
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              height: '100%',
              width: '4px',
              background: 'rgba(0, 0, 0, 0.1)',
              cursor: 'col-resize',
              userSelect: 'none',
              touchAction: 'none'
            }}
          />
        )}
      </div>
      {showFilters &&
        header.column.getCanFilter() &&
        header.column.id !== 'action' && (
          <div className="p-0 d-flex align-items-center gap-1">
            {dateColumns.includes(header.column.id.toLowerCase()) ? (
              <input
                type="date"
                value={
                  header.column.getFilterValue()
                    ? format(
                        parse(
                          header.column.getFilterValue(),
                          'dd-MM-yyyy',
                          new Date()
                        ), // Convert stored value to ISO format
                        'yyyy-MM-dd'
                      )
                    : ''
                }
                onChange={e => {
                  const value = e.target.value;
                  if (value) {
                    const formattedDate = format(new Date(value), 'dd-MM-yyyy');
                    header.column.setFilterValue(formattedDate);
                  } else {
                    header.column.setFilterValue('');
                  }
                }}
                className="form-control form-control-sm"
                style={{ minWidth: '100px' }}
              />
            ) : (
              <input
                type="text"
                value={header.column.getFilterValue() ?? ''}
                onChange={e => header.column.setFilterValue(e.target.value)}
                placeholder="Search..."
                className="form-control form-control-sm"
                style={{ minWidth: '100px' }}
              />
            )}
            {isFirstFilterableColumn() && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="tooltip-exclamation">
                    All column level filter applies only to current page data.
                  </Tooltip>
                }
              >
                <span>
                  <BsExclamationCircle />
                </span>
              </OverlayTrigger>
            )}
          </div>
        )}
    </div>
  );
};

// Helper function for table body cells
export const getBodyCellPinningStyles = (column, allColumns = []) => {
  const baseStyles = getCommonPinningStyles(column, allColumns);
  return {
    ...baseStyles,
    backgroundColor:
      baseStyles.backgroundColor === 'rgb(97 6 79)'
        ? 'white'
        : baseStyles.backgroundColor,
    color: 'inherit'
  };
};
